import React, { useEffect, useState } from 'react';
import Switch from '@material-ui/core/Switch';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import NotificationsIcon from '@material-ui/icons/Notifications';
import { CustomBreadcrumbs } from 'candy-commons-components';
import { useDataProvider, useNotify, useTranslate } from 'react-admin';

const RESOURCE = 'manual-badge-override';
const SECTIONS = ['promo', 'news', 'more'];

const ManualBadgeOverrideList = ({ permissions }) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const translation = useTranslate();
    const [rows, setRows] = useState({});
    const [loading, setLoading] = useState(true);
    const canUpdate = permissions?.[RESOURCE]?.update;
    const canCreate = permissions?.[RESOURCE]?.create;

    useEffect(() => {
        dataProvider
            .getList(RESOURCE, {
                pagination: { page: 1, perPage: 10 },
                sort: { field: 'section', order: 'ASC' },
                filter: {},
            })
            .then(({ data }) => {
                const indexed = {};
                (data || []).forEach((item) => {
                    const key = item.section || item.id;
                    if (key) indexed[key] = item;
                });
                setRows(indexed);
                setLoading(false);
            })
            .catch(() => {
                notify('Error loading badge overrides', 'warning');
                setLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleToggle = async (section) => {
        const current = rows[section];
        if (!canUpdate && !canCreate) return;

        // Record missing: first toggle creates it as active=true (no create form).
        if (!current?.id && !current?.section) {
            if (!canCreate) {
                notify('Badge override record is missing — create it first', 'warning');
                return;
            }
            const optimistic = {
                id: section,
                section,
                active: true,
                activatedAt: new Date().toISOString(),
            };
            setRows((prev) => ({ ...prev, [section]: optimistic }));
            try {
                const { data: created } = await dataProvider.create(RESOURCE, {
                    data: { section, active: true },
                });
                setRows((prev) => ({
                    ...prev,
                    [section]: created || optimistic,
                }));
            } catch {
                setRows((prev) => {
                    const next = { ...prev };
                    delete next[section];
                    return next;
                });
                notify('Failed to create badge override', 'warning');
            }
            return;
        }

        const optimistic = { ...current, active: !current.active };
        setRows((prev) => ({ ...prev, [section]: optimistic }));
        try {
            const { data: updated } = await dataProvider.update(RESOURCE, {
                id: section,
                data: {
                    // id MUST be in the body: cms dynamo.service builds SK from body, not URL
                    id: section,
                    section,
                    active: !current.active,
                    currentActive: current.active === true,
                    activatedAt: current.activatedAt ?? null,
                },
                previousData: current,
            });
            setRows((prev) => ({
                ...prev,
                [section]: updated || optimistic,
            }));
        } catch {
            setRows((prev) => ({ ...prev, [section]: current }));
            notify('Failed to update badge override', 'warning');
        }
    };

    const breadcrumbsData = [
        {
            text: translation(`resources.${RESOURCE}.name`),
            url: '',
            icon: <NotificationsIcon />,
        },
    ];

    if (loading) return <CircularProgress />;

    return (
        <>
            <CustomBreadcrumbs data={breadcrumbsData} />
            <div style={{ padding: 16 }}>
                {SECTIONS.map((section) => {
                    const row = rows[section] ?? {
                        active: false,
                        activatedAt: null,
                    };
                    return (
                        <div
                            key={section}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '12px 0',
                                borderBottom: '1px solid #eee',
                            }}
                        >
                            <Typography
                                style={{
                                    flex: 1,
                                    textTransform: 'capitalize',
                                }}
                            >
                                {section}
                            </Typography>
                            <Switch
                                checked={!!row.active}
                                onChange={() => handleToggle(section)}
                                disabled={!canUpdate && !canCreate}
                                color="primary"
                            />
                            {row.active && row.activatedAt && (
                                <Typography
                                    variant="caption"
                                    style={{
                                        marginLeft: 16,
                                        color: '#666',
                                    }}
                                >
                                    Active since:{' '}
                                    {new Date(
                                        row.activatedAt,
                                    ).toLocaleString()}
                                </Typography>
                            )}
                        </div>
                    );
                })}
            </div>
        </>
    );
};

export default ManualBadgeOverrideList;
